import { Box, Tabs, Tab } from '@mui/material';
import { useEffect, useState } from 'react';
// import { EventTable } from '../../components/EventTable';
import { PageTitle } from '../../components/PageTitle';
import { AnimatedBox } from '../../components/AnimatedBox';
import { EmptyState } from '../../components/EmptyState';
import { LoadingState } from '../../components/LoadingState';
import { AddEventButton } from '../../components/AddEventButton';
// import EventDialog from '../../components/EventDialog';
import { Enterprise, ENTERPRISE_COLLECTION, User, USER_COLLECTION, type Board } from '../../type';
import { DialogMode } from '../../type/DialogMode';
import { collection, getFirestore, onSnapshot, orderBy, query } from 'firebase/firestore';
import { BoardTable } from '../../components/BoardTable';
import BoardDialog from '../../components/BoardDialog';

interface BoardContentProps {
  boards: Board[];
  loading: boolean;
  error: string | null;
  addBoard: (board: Board) => Promise<void>;
  updateBoard: (board: Board) => Promise<void>;
  deleteBoard: (boardId: string) => Promise<void>;
}

const BoardContent = ({
  boards,
  loading,
  error,
  addBoard,
  updateBoard,
  deleteBoard,
}: BoardContentProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [dialogMode, setDialogMode] = useState<DialogMode>(DialogMode.CREATE);
  const [selectedBoard, setSelectedBoard] = useState<Board | undefined>(undefined);
  const [independentUsers, setIndependentUsers] = useState<User[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);

  useEffect(() => {
    const db = getFirestore();
    const usersRef = collection(db, USER_COLLECTION);
    const q = query(usersRef, orderBy('createdAt', 'desc'));

    onSnapshot(q, (snapshot) => {
      const i_users: User[] = [];
      const e_users: User[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as User;
        if (data.uid?.length > 0 && data.displayName?.length > 0) {
          if (data.isEnterpriseUser && data.enterpriseId) {
            e_users.push(data);
          } else {
            i_users.push(data);
          }
        }
      });

      setIndependentUsers(i_users);
      setEnterpriseUsers(e_users);
    });

    const enterprisesRef = collection(db, ENTERPRISE_COLLECTION);
    const eq = query(enterprisesRef, orderBy('createdAt', 'desc'));

    onSnapshot(eq, (snapshot) => {
      const list: Enterprise[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as Enterprise;
        list.push(data);
      });

      setEnterprises(list);
    });
  }, []);

  const handleAddBoard = () => {
    setDialogMode(DialogMode.CREATE);
    setSelectedBoard(undefined);
    setIsDialogOpen(true);
  };

  const handleEditBoard = (board: Board) => {
    setDialogMode(DialogMode.EDIT);
    setSelectedBoard(board);
    setIsDialogOpen(true);
  };

  const handleSaveBoard = async (board: Board) => {
    try {
      if (dialogMode === DialogMode.EDIT) {
        await updateBoard(board);
      } else {
        await addBoard(board);
      }
      handleCloseDialog();
    } catch (error) {
      alert('게시판 저장에 실패했습니다.');
    }
  };

  const handleDeleteBoard = async (boardId: string) => {
    try {
      const confirmed = window.confirm('정말로 이 게시판을 삭제하시겠습니까?');
      if (confirmed) {
        await deleteBoard(boardId);
        handleCloseDialog();
      }
    } catch (error) {
      alert('게시판 삭제에 실패했습니다.');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedBoard(undefined);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <AnimatedBox>
        <PageTitle>게시판 관리</PageTitle>
        <LoadingState />
      </AnimatedBox>
    );
  }

  return (
    <AnimatedBox>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row', 
        alignItems: 'center', 
        justifyContent: 'space-between',
      }}>
        <PageTitle>게시판 관리</PageTitle>
        <AddEventButton title='새 게시글 추가' onAddClick={handleAddBoard} />
      </Box>

      {error && (
        <Box sx={{ mb: 3, color: 'error.main' }}>
          {error}
        </Box>
      )}

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="게시판 목록" />
      </Tabs>

      {activeTab === 0 && (
        // 게시판 목록 탭
        <Box>
          {boards.length === 0 ? (
            <EmptyState message="아직 등록된 게시판이 없습니다." />
          ) : (
            <BoardTable
              data={boards}
              onBoardSelect={handleEditBoard}
            />
          )}
        </Box>
      )}

      {isDialogOpen && (
        <BoardDialog
          open={isDialogOpen}
          mode={dialogMode}
          board={selectedBoard}
          onClose={handleCloseDialog}
          onSave={handleSaveBoard}
          onDelete={handleDeleteBoard}
          i_users={independentUsers}
          e_users={enterpriseUsers}
          ent={enterprises}
        />
      )}
    </AnimatedBox>
  );
};

export default BoardContent;